import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import type { ToolConstructorArgs } from './Tool';
import { VideoStateTool } from './VideoStateTool';

import { setAssetIntroExitTimesOperation } from 'js/src/luminary/asset-timing-operations';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    getEditableTextAssetsFromGroup,
    getRootAssetGroup,
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    isVoiceDrivenSceneGroup,
    translateTranscriptTimeToSceneGroupTime,
    translateVideoTimeToSceneGroupTime,
} from 'js/src/luminary/luminary-selectors';
import {
    getVoicedrivenSceneDuration,
    getVoicedrivenSceneEndTime,
    getVoicedrivenSceneStartTime,
} from 'js/src/luminary/voicedriven-scenes';

export const legacySetAssetTimingSchema = z.object({
    scene_index: z.number().describe('The scene index (0, 1, 2, etc.)'),
    asset_name: z.string().describe('The name of the text asset (e.g., text_1, text_2)'),
    start_word_index: z
        .number()
        .min(0)
        .describe('The index of the transcript word when the asset should appear (0-based)'),
});
export type SetAssetTimingArgs = z.infer<typeof legacySetAssetTimingSchema>;

export class LegacySetAssetTimingTool extends VideoStateTool<
    typeof legacySetAssetTimingSchema,
    ToolConstructorArgs
> {
    protected override readonly schema = legacySetAssetTimingSchema;
    protected override readonly toolName = TOOL_NAMES.setAssetTiming;
    protected override readonly toolDescription =
        'Set when an asset appears based on transcript word index. ' +
        'The asset will appear when the specified word begins and remain visible until the end of the scene.';

    public override getToolDisplayName(): string {
        return 'Setting asset timing';
    }

    public get statusIcon(): string {
        return 'schedule';
    }

    public async execute(args: SetAssetTimingArgs, context: ToolExecutionContext) {
        const { scene_index, asset_name, start_word_index } = args;
        const { parentActionGroup } = context;

        try {
            if (scene_index < 0 || scene_index >= this.luminary.scenes.length) {
                return `Error: scene_index ${scene_index} is out of bounds for the current video`;
            }

            const scene = this.luminary.scenes[scene_index];
            if (!scene) {
                return `Error: No scene found at index ${scene_index}`;
            }

            const sceneGroup = scene.sceneGroup;
            if (!sceneGroup || !isVoiceDrivenSceneGroup(sceneGroup)) {
                return 'Error: This scene is not part of a voice-driven scene group';
            }

            const transcript = sceneGroup.captions?.asset?.transcript;
            if (!transcript || !transcript.words || transcript.words.length === 0) {
                return 'Error: No transcript words found for this scene group';
            }

            const asset = scene.getAssetByName(asset_name);
            if (!asset) {
                return `Error: No asset found with name ${asset_name} in scene ${scene_index}`;
            }

            if (!asset.isEditable) {
                return `Error: Asset ${asset_name} in scene ${scene_index} is not editable`;
            }

            if (start_word_index < 0 || start_word_index >= transcript.words.length) {
                return `Error: start_word_index ${start_word_index} is out of bounds (0-${transcript.words.length - 1})`;
            }

            const startWord = transcript.words[start_word_index];

            const voicedrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
            const voicedrivenBaseAssetSourceDuration =
                getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
            const startWordStartTime = translateTranscriptTimeToSceneGroupTime(
                startWord.startTime,
                voicedrivenAssetPlaybackRegions,
                voicedrivenBaseAssetSourceDuration,
            );

            // Validate that the start word overlaps with the scene's time range
            const sceneStartInGroup = translateVideoTimeToSceneGroupTime(
                getVoicedrivenSceneStartTime(scene),
                sceneGroup.startTime,
            );
            const sceneEndInGroup = translateVideoTimeToSceneGroupTime(
                getVoicedrivenSceneEndTime(scene),
                sceneGroup.startTime,
            );

            if (startWordStartTime >= sceneEndInGroup) {
                return `Error: Word ${start_word_index} ("${startWord.word}") is outside scene ${scene_index}'s time range`;
            }

            const introTime = Math.max(0, startWordStartTime - sceneStartInGroup);

            // Validate staggered entry for grouped assets
            const rootGroup = getRootAssetGroup(asset);
            if (rootGroup) {
                const siblings = getEditableTextAssetsFromGroup(rootGroup);
                const assetIndex = siblings.findIndex((s) => s.name === asset.name);

                if (assetIndex > 0) {
                    // Check that all previous siblings have an earlier or equal intro time
                    for (let i = 0; i < assetIndex; i++) {
                        const previousSibling = siblings[i];
                        const previousIntroTime = previousSibling.introAnimationOffset;

                        if (previousIntroTime > introTime) {
                            return `Error: Asset "${asset_name}" must appear after "${previousSibling.name}" (which appears later at ${previousIntroTime.toFixed(0)}ms). Set timing for earlier assets first, or use a later start_word_index.`;
                        }
                    }
                }
            }

            // Always exit at end of scene
            const exitTime = getVoicedrivenSceneDuration(scene);

            // Use the existing luminary operation
            const { setAssetIntroExitTimes } = makeOperationsCallable(
                { setAssetIntroExitTimesOperation },
                this.luminary,
                this.dispatch,
                this.getState,
            );

            setAssetIntroExitTimes({
                timingSpecifications: [
                    {
                        sceneId: scene.id,
                        assetId: asset.id,
                        introTime,
                        exitTime,
                    },
                ],
                operatingOnMainLuminary: true,
                systemTriggered: true,
                parentActionGroup,
            });

            return `Success: Set timing for "${asset_name}" - appears at word ${start_word_index} ("${startWord.word}"), visible until end of scene`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting asset timing: ${errorMessage}`;
        }
    }
}
